import {ReviewState, ReviewStore} from "./review.interface";
import {DifficultyLevel} from "../common/api";
import {CardMarkupViewerState} from "../markup/cardMarkupViewer/cardMarkupViewer.interface";
import {createAction, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {reviewInitialState} from "./review.constants";
import {getActionName} from "../utils/getActionName";
import {markupFormatter, ReviewCard} from "../common";
import {ISubcard} from "../cards/multicards.interface";
import {getNextSubcardIndex} from "./utils/multicard.utils";

const sliceName = 'review';

interface SetStatePayload {
    reviewState: ReviewState,
    markupViewerState: CardMarkupViewerState,
}

export interface SubmitPayload {
    intervalMinutes: number;
    difficulty?: DifficultyLevel;
}

const reviewSlice = createSlice({
    name: sliceName,
    initialState: reviewInitialState,
    reducers: {
        setState(state: ReviewStore, action: PayloadAction<SetStatePayload>) {
            state.state = action.payload.reviewState;
            state.markupState = action.payload.markupViewerState;
        },

        nextCard(state: ReviewStore, action: PayloadAction<ReviewCard>) {
            const payload = action.payload;

            if (payload.card) {
                state.cardId = payload.card.cardId;
                state.markupState = CardMarkupViewerState.OnlyQuestion;
                state.state = ReviewState.OnlyQuestion;
                
                // Reset multicard state
                const markupTree = markupFormatter.parse(payload.card.markup);
                state.isMultiCard = markupTree.hasSubCards;
                state.allSubcards = markupTree.subCards;
                state.viewedSubcardIndices = [];
                state.subcardTimings = [];
                state.subcardCorrectness = [];
                
                // If it's a multicard, select the first random subcard
                if (state.isMultiCard && state.allSubcards.length > 0) {
                    const randomIndex = getNextSubcardIndex(state.allSubcards, state.viewedSubcardIndices);
                    if (randomIndex !== undefined) {
                        state.currentSubcardIndex = randomIndex;
                        state.viewedSubcardIndices.push(randomIndex);
                        state.selectedSubcards = [state.allSubcards[randomIndex]];
                        state.isLastSubcard = false;
                        state.showSubcardButtons = true;
                        state.state = ReviewState.SubcardQuestion;
                        state.subcardReviewStartTime = Date.now();
                    }
                } else {
                    // Regular card
                    state.selectedSubcards = [];
                    state.currentSubcardIndex = 0;
                    state.isLastSubcard = false;
                    state.showSubcardButtons = false;
                }
                
                if (payload.dailyStat) {
                    state.cardNumberInReview = payload.dailyStat.reviewedToday + 1;
                    state.totalCardsForReview = payload.dailyStat.reviewedToday + payload.dailyStat.totalCardsForReview;
                }
            }
        },

        setCompletedState(state: ReviewStore) {
            state.state = ReviewState.Completed;
            state.markupState = undefined;
            state.cardId = undefined;
        },

        openExtraActionsMenu(state: ReviewStore) {
            state.isExtraActionsMenuOpen = true;
        },

        closeExtraActionsMenu(state: ReviewStore) {
            state.isExtraActionsMenuOpen = false;
        },
        
        // MultiCard specific reducers
        showSubcardAnswer(state: ReviewStore) {
            if (state.isMultiCard) {
                state.state = ReviewState.SubcardAnswer;
                state.markupState = CardMarkupViewerState.Full;
                state.showSubcardButtons = true;
            }
        },
        
        nextSubcard(state: ReviewStore, _action: PayloadAction<NextSubcardPayload>) {
            if (!state.isMultiCard || state.allSubcards.length === 0) {
                return;
            }
            
            // Record timing data for the current subcard
            if (state.subcardReviewStartTime !== undefined) {
                const timeSpent = Date.now() - state.subcardReviewStartTime;
                state.subcardTimings.push(timeSpent);
            }
            
            // Select next random subcard
            const randomIndex = getNextSubcardIndex(state.allSubcards, state.viewedSubcardIndices);
            if (randomIndex !== undefined) {
                state.currentSubcardIndex = randomIndex;
                state.viewedSubcardIndices.push(randomIndex);
                // Accumulate viewed subcards instead of replacing
                state.selectedSubcards.push(state.allSubcards[randomIndex]);
                state.isLastSubcard = false;
                state.state = ReviewState.SubcardQuestion;
                state.markupState = CardMarkupViewerState.OnlyQuestion;
                state.showSubcardButtons = false;
                state.subcardReviewStartTime = Date.now();
            } else {
                // All subcards have been reviewed, move to the final answer state
                state.state = ReviewState.Answer;
                state.markupState = CardMarkupViewerState.Full;
            }
        },

        resetSubcardIndex(state: ReviewStore) {
            if (state.isMultiCard) {
                state.currentSubcardIndex = 0;
                state.isLastSubcard = state.selectedSubcards.length <= 1;
                state.state = ReviewState.SubcardQuestion;
                state.markupState = CardMarkupViewerState.OnlyQuestion;
                state.showSubcardButtons = false;
                state.subcardReviewStartTime = Date.now();
                state.subcardTimings = [];
                state.subcardCorrectness = [];
            }
        },

        // Ends the subcard loop early and switches to the whole-card difficulty
        // rating. The currently shown subcard stays ungraded here and is graded
        // by that final rating at submit time (see submitReviewSaga).
        finishSubcards(state: ReviewStore) {
            if (state.isMultiCard) {
                state.state = ReviewState.Answer;
                state.markupState = CardMarkupViewerState.Full;
            }
        },

        // Records correctness for the currently shown subcard, then advances to
        // the next random subcard (or to the final card rating when none remain).
        markSubcardResult(state: ReviewStore, action: PayloadAction<boolean>) {
            if (!state.isMultiCard || state.allSubcards.length === 0) {
                return;
            }

            state.subcardCorrectness.push(action.payload);

            if (state.subcardReviewStartTime !== undefined) {
                state.subcardTimings.push(Date.now() - state.subcardReviewStartTime);
            }

            const randomIndex = getNextSubcardIndex(state.allSubcards, state.viewedSubcardIndices);
            if (randomIndex !== undefined) {
                state.currentSubcardIndex = randomIndex;
                state.viewedSubcardIndices.push(randomIndex);
                state.selectedSubcards.push(state.allSubcards[randomIndex]);
                state.isLastSubcard = false;
                state.state = ReviewState.SubcardQuestion;
                state.markupState = CardMarkupViewerState.OnlyQuestion;
                state.showSubcardButtons = false;
                state.subcardReviewStartTime = Date.now();
            } else {
                // All subcards reviewed -> final whole-card rating.
                state.state = ReviewState.Answer;
                state.markupState = CardMarkupViewerState.Full;
            }
        },
    }
});

export const reviewReducer = reviewSlice.reducer;

export interface NextSubcardPayload {
    subcardIndex: number;
    timeSpent: number;
    subcard: ISubcard;
}

export interface CardIdPayload {
    cardId?: string;
}

export const reviewActions = {
    ...reviewSlice.actions,
    showAnswer: createAction(getActionName(sliceName, 'showAnswer')),
    submit: createAction<SubmitPayload>(getActionName(sliceName, 'submit')),
    loadNext: createAction(getActionName(sliceName, 'loadNext'), (cardId?: string) => ({payload: {cardId}})),
    initialize: createAction(getActionName(sliceName, 'initialize'), (cardId?: string) => ({payload: {cardId}})),
    // MultiCard specific actions
    showSubcardAnswer: createAction(getActionName(sliceName, 'showSubcardAnswer')),
    nextSubcard: createAction<NextSubcardPayload>(getActionName(sliceName, 'nextSubcard')),
};
